import React, { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Alert,
    Snackbar,
    CircularProgress,
} from '@mui/material';
import {
    FilterList as FilterListIcon,
    Clear as ClearIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    AttachFile as AttachFileIcon,
    ArrowForward as BackIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import useFetch from '../../../../../hooks/useFetch';
import useDeleteItem from '../../../../../hooks/useDeleteItem';
import RTL from '../../../../../utils/RTL';
import { toPersianDigits } from '../../../../../utils/convertNumbers';
import { API } from '../../../../../utils/apiRoutes';
import StandardTable from '../../../../common/table/StandardTable';
import PersianDatePicker from '../../../../common/PersianDatePicker';
import CashboxReconciliationForm from './CashboxReconciliationForm';
import api from '../../../../../config/axiosConfig';

function CashboxReconciliation() {
    const theme = useTheme();
    const [filters, setFilters] = useState({
        is_resolved: '',
        start_date: '',
        end_date: '',
    });
    const [formDialogOpen, setFormDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
    const [selectedReconciliation, setSelectedReconciliation] = useState(null);
    const [reconciliationToDelete, setReconciliationToDelete] = useState(null);
    const [reconciliationToResolve, setReconciliationToResolve] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [refreshToken, setRefreshToken] = useState(0);

    const { deleteItem, isLoading: isDeleting } = useDeleteItem();

    // Build API params
    const apiParams = useMemo(() => {
        const params = {};
        if (filters.is_resolved !== '') params.is_resolved = filters.is_resolved === 'true' ? 'true' : 'false';
        if (filters.start_date) {
            const dateStr = filters.start_date instanceof Date 
                ? filters.start_date.toISOString().split('T')[0] 
                : filters.start_date;
            params.start_date = dateStr;
        }
        if (filters.end_date) {
            const dateStr = filters.end_date instanceof Date 
                ? filters.end_date.toISOString().split('T')[0] 
                : filters.end_date;
            params.end_date = dateStr;
        }
        return params;
    }, [filters]);

    const { data, loading, error, refetch } = useFetch(API.cashboxReconciliations, { 
        params: apiParams,
    });

    const reconciliations = data?.results || data || [];
    const totalCount = data?.count || reconciliations.length;
    const hasActiveFilters = Object.values(filters).some(v => v !== '');

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const handleClearFilters = () => {
        setFilters({
            is_resolved: '',
            start_date: '',
            end_date: '',
        });
    };

    const handleCreate = () => {
        setSelectedReconciliation(null);
        setFormDialogOpen(true);
    };

    const handleEdit = (reconciliation) => {
        setSelectedReconciliation(reconciliation);
        setFormDialogOpen(true);
    };

    const handleDelete = (reconciliation) => {
        setReconciliationToDelete(reconciliation);
        setDeleteDialogOpen(true);
    };

    const handleResolve = (reconciliation) => {
        setReconciliationToResolve(reconciliation);
        setResolveDialogOpen(true);
    };

    const confirmDelete = async () => {
        if (!reconciliationToDelete) return;
        try {
            await deleteItem(`${API.cashboxReconciliations}${reconciliationToDelete.id}/`);
            setSnackbar({ 
                open: true, 
                message: 'تطبیق صندوق با موفقیت حذف شد.', 
                severity: 'success' 
            });
            refetch();
            setRefreshToken(prev => prev + 1);
        } catch (err) {
            setSnackbar({ 
                open: true, 
                message: err?.response?.data?.detail || 'خطا در حذف تطبیق صندوق', 
                severity: 'error' 
            });
        } finally {
            setDeleteDialogOpen(false);
            setReconciliationToDelete(null);
        }
    };

    const confirmResolve = async () => {
        if (!reconciliationToResolve) return;
        try {
            await api.post(`${API.cashboxReconciliations}${reconciliationToResolve.id}/resolve/`);
            setSnackbar({ 
                open: true, 
                message: 'تطبیق صندوق با موفقیت بررسی شد.', 
                severity: 'success' 
            });
            refetch();
            setRefreshToken(prev => prev + 1);
        } catch (err) {
            setSnackbar({ 
                open: true, 
                message: err?.response?.data?.detail || 'خطا در بررسی تطبیق صندوق', 
                severity: 'error' 
            });
        } finally {
            setResolveDialogOpen(false);
            setReconciliationToResolve(null);
        }
    };

    const handleFormSuccess = () => {
        setFormDialogOpen(false);
        setSelectedReconciliation(null);
        refetch();
        setRefreshToken(prev => prev + 1);
        setSnackbar({ 
            open: true, 
            message: selectedReconciliation ? 'تطبیق صندوق با موفقیت ویرایش شد.' : 'تطبیق صندوق با موفقیت ثبت شد.', 
            severity: 'success' 
        });
    };

    const formatCurrency = (amount) => {
        return toPersianDigits(new Intl.NumberFormat('fa-IR').format(amount || 0));
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return toPersianDigits(date.toLocaleDateString('fa-IR'));
        } catch {
            return dateString;
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return toPersianDigits(date.toLocaleString('fa-IR'));
        } catch {
            return dateString;
        }
    };

    const columns = [
        {
            id: 'shift_date',
            label: 'تاریخ\nشیفت',
            render: (reconciliation) => (
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {formatDate(reconciliation.shift_date)}
                </Typography>
            )
        },
        {
            id: 'opening_balance',
            label: 'موجودی\nابتدای شیفت',
            priority: 'secondary',
            render: (reconciliation) => (
                <Typography variant="body2">
                    {formatCurrency(reconciliation.opening_balance || 0)} تومان
                </Typography>
            )
        },
        {
            id: 'expected_total',
            label: 'مبلغ\nانتظاری',
            priority: 'secondary',
            render: (reconciliation) => (
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {formatCurrency(reconciliation.expected_total || 0)} تومان
                </Typography>
            )
        },
        {
            id: 'closing_balance',
            label: 'موجودی\nانتهای شیفت',
            render: (reconciliation) => (
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(reconciliation.closing_balance || 0)} تومان
                </Typography>
            )
        },
        {
            id: 'actual_difference',
            label: 'تفاوت',
            compact: true,
            render: (reconciliation) => {
                const diff = reconciliation.actual_difference || 0;
                const isPositive = diff > 0;
                const isZero = diff === 0;
                return (
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            fontWeight: 'bold',
                            color: isZero ? 'success.main' : (isPositive ? 'info.main' : 'error.main')
                        }}
                    >
                        {isPositive ? '+' : ''}{formatCurrency(diff)} تومان
                    </Typography>
                );
            }
        },
        {
            id: 'is_resolved',
            label: 'وضعیت',
            compact: true,
            render: (reconciliation) => (
                <Chip
                    label={reconciliation.is_resolved ? 'بررسی شده' : 'نیازمند بررسی'}
                    size="small"
                    color={reconciliation.is_resolved ? 'success' : 'warning'}
                    icon={!reconciliation.is_resolved && reconciliation.actual_difference !== 0 ? <WarningIcon /> : undefined}
                />
            )
        },
        {
            id: 'resolved_by_name',
            label: 'بررسی‌کننده',
            align: 'start',
            priority: 'secondary',
            render: (reconciliation) => (
                <Box>
                    <Typography variant="body2">
                        {reconciliation.resolved_by_name || '-'}
                    </Typography>
                    {reconciliation.resolved_at && (
                        <Typography variant="caption" color="text.secondary">
                            {formatDateTime(reconciliation.resolved_at)}
                        </Typography>
                    )}
                </Box>
            )
        },
        {
            id: 'supporting_files',
            label: 'فایل‌ها',
            compact: true,
            priority: 'secondary',
            render: (reconciliation) => {
                const files = reconciliation.supporting_files || [];
                if (files.length === 0) {
                    return <Typography variant="body2" color="text.secondary">-</Typography>;
                }
                return (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {files.map((file, index) => (
                            <IconButton
                                key={index}
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(file, '_blank');
                                }}
                                aria-label={`مشاهده فایل ${index + 1}`}
                            >
                                <AttachFileIcon fontSize="small" />
                            </IconButton>
                        ))}
                    </Box>
                );
            }
        },
        {
            id: 'actions',
            label: 'عملیات',
            compact: true,
            render: (reconciliation) => (
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    {!reconciliation.is_resolved && (
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleResolve(reconciliation);
                            }}
                            color="success"
                            aria-label="بررسی"
                        >
                            <CheckCircleIcon fontSize="small" />
                        </IconButton>
                    )}
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(reconciliation);
                        }}
                        color="primary"
                        aria-label="ویرایش"
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(reconciliation);
                        }}
                        color="error"
                        aria-label="حذف"
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            )
        },
    ];

    return (
        <RTL>
            <Box sx={{ p: 3 }}>
                {formDialogOpen ? (
                    /* Full-page create/edit form (drill-down) */
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                            <Button
                                startIcon={<BackIcon />}
                                variant="outlined"
                                onClick={() => {
                                    setFormDialogOpen(false);
                                    setSelectedReconciliation(null);
                                }}
                            >
                                بازگشت به فهرست
                            </Button>
                            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                {selectedReconciliation ? 'ویرایش تطبیق صندوق' : 'ثبت تطبیق صندوق جدید'}
                            </Typography>
                        </Box>
                        <Paper sx={{ p: 3 }}>
                            <CashboxReconciliationForm
                                reconciliation={selectedReconciliation}
                                onSuccess={handleFormSuccess}
                                onCancel={() => {
                                    setFormDialogOpen(false);
                                    setSelectedReconciliation(null);
                                }}
                            />
                        </Paper>
                    </Box>
                ) : (
                <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        تطبیق صندوق
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleCreate}
                        sx={{ mr: 'auto' }}
                    >
                        ثبت تطبیق جدید
                    </Button>
                </Box>

                {/* Filters */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <FilterListIcon sx={{ mr: 1 }} />
                        <Typography variant="h6">فیلترها</Typography>
                        {hasActiveFilters && (
                            <Button
                                size="small"
                                startIcon={<ClearIcon />}
                                onClick={handleClearFilters}
                                sx={{ mr: 'auto' }}
                            >
                                پاک کردن فیلترها
                            </Button>
                        )}
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Grid 
                        container 
                        spacing={2}
                        sx={{
                            '& .MuiTextField-root': { m: 0 },
                            '& .MuiFormControl-root': { m: 0 },
                        }}
                    >
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>وضعیت بررسی</InputLabel>
                                <Select
                                    value={filters.is_resolved}
                                    label="وضعیت بررسی"
                                    onChange={(e) => handleFilterChange('is_resolved', e.target.value || '')}
                                >
                                    <MenuItem value="">همه</MenuItem>
                                    <MenuItem value="true">بررسی شده</MenuItem>
                                    <MenuItem value="false">نیازمند بررسی</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <PersianDatePicker
                                label="از تاریخ"
                                value={filters.start_date ? new Date(filters.start_date) : null}
                                onChange={(date) => {
                                    if (date && !isNaN(date.getTime())) {
                                        handleFilterChange('start_date', date.toISOString().split('T')[0]);
                                    } else {
                                        handleFilterChange('start_date', '');
                                    }
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <PersianDatePicker
                                label="تا تاریخ"
                                value={filters.end_date ? new Date(filters.end_date) : null}
                                onChange={(date) => {
                                    if (date && !isNaN(date.getTime())) {
                                        handleFilterChange('end_date', date.toISOString().split('T')[0]);
                                    } else {
                                        handleFilterChange('end_date', '');
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>
                </Paper>

                {/* Unresolved Warning */}
                {!loading && reconciliations.filter(r => !r.is_resolved && r.actual_difference !== 0).length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {toPersianDigits(reconciliations.filter(r => !r.is_resolved && r.actual_difference !== 0).length)} تطبیق با تفاوت نیازمند بررسی است.
                    </Alert>
                )}

                {/* Loading State */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 8 }}>
                        <CircularProgress />
                    </Box>
                )}

                {/* Results Count */}
                {!loading && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            تعداد نتایج: {toPersianDigits(totalCount)}
                        </Typography>
                    </Box>
                )}

                {/* Reconciliations Table */}
                {!loading && (
                    <StandardTable
                        columns={columns}
                        rows={reconciliations}
                        loading={false}
                        error={error}
                        emptyText="تطبیق صندوقی یافت نشد."
                        ariaLabel="جدول تطبیق‌های صندوق"
                        onRowClick={handleEdit}
                    />
                )}
                </>
                )}

                {/* Delete Confirmation Dialog */}
                <Dialog
                    open={deleteDialogOpen}
                    onClose={() => {
                        setDeleteDialogOpen(false);
                        setReconciliationToDelete(null);
                    }}
                >
                    <DialogTitle>تأیید حذف</DialogTitle>
                    <DialogContent>
                        <Typography>
                            آیا از حذف این تطبیق صندوق اطمینان دارید؟ این عمل قابل بازگشت نیست.
                        </Typography>
                        {reconciliationToDelete && (
                            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    تاریخ شیفت: {formatDate(reconciliationToDelete.shift_date)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    موجودی انتهای شیفت: {formatCurrency(reconciliationToDelete.closing_balance)} تومان
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    تفاوت: {formatCurrency(reconciliationToDelete.actual_difference)} تومان
                                </Typography>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button 
                            onClick={() => {
                                setDeleteDialogOpen(false);
                                setReconciliationToDelete(null);
                            }}
                        >
                            انصراف
                        </Button>
                        <Button 
                            onClick={confirmDelete} 
                            color="error" 
                            variant="contained"
                            disabled={isDeleting}
                        >
                            حذف
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Resolve Confirmation Dialog */}
                <Dialog
                    open={resolveDialogOpen}
                    onClose={() => {
                        setResolveDialogOpen(false);
                        setReconciliationToResolve(null);
                    }}
                >
                    <DialogTitle>بررسی تطبیق صندوق</DialogTitle>
                    <DialogContent>
                        <Typography>
                            آیا از بررسی این تطبیق صندوق اطمینان دارید؟
                        </Typography>
                        {reconciliationToResolve && (
                            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    تاریخ شیفت: {formatDate(reconciliationToResolve.shift_date)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    موجودی انتهای شیفت: {formatCurrency(reconciliationToResolve.closing_balance)} تومان
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    مبلغ انتظاری: {formatCurrency(reconciliationToResolve.expected_total)} تومان
                                </Typography>
                                <Typography 
                                    variant="body2" 
                                    color={reconciliationToResolve.actual_difference !== 0 ? 'error.main' : 'success.main'}
                                    sx={{ fontWeight: 'bold', mt: 1 }}
                                >
                                    تفاوت: {formatCurrency(reconciliationToResolve.actual_difference)} تومان
                                </Typography>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button 
                            onClick={() => {
                                setResolveDialogOpen(false);
                                setReconciliationToResolve(null);
                            }}
                        >
                            انصراف
                        </Button>
                        <Button 
                            onClick={confirmResolve} 
                            color="success" 
                            variant="contained"
                        >
                            بررسی
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Snackbar */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert 
                        onClose={() => setSnackbar({ ...snackbar, open: false })} 
                        severity={snackbar.severity}
                        sx={{ width: '100%' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </RTL>
    );
}

export default CashboxReconciliation;

